'use client';
import React, { useState } from 'react';

interface Testimonial {
  name: string;
  location: string;
  flag: string;
  tour: string;
  rating: number;
  image: string;
  alt: string;
  text: string;
}

const testimonials: Testimonial[] = [
{
  name: 'Sarah & Michael Cohen',
  location: 'New York, USA',
  flag: '🇺🇸',
  tour: 'Jewish Heritage Tour',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&q=80',
  alt: 'Sarah Cohen, satisfied customer from New York who took a Jewish Heritage Tour in Morocco with David',
  text: 'David gave us the most extraordinary experience of our lives. As Jewish travelers, we were deeply moved by the synagogues, mellahs, and cemeteries he showed us. His knowledge of Moroccan Jewish history is simply unmatched. We felt completely safe, pampered, and profoundly connected to our heritage. We will return!'
},
{
  name: 'James & Patricia Williams',
  location: 'London, UK',
  flag: '🇬🇧',
  tour: 'Private Morocco Tour',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80',
  alt: 'James Williams, satisfied customer from London who took a 7-day private luxury tour in Morocco',
  text: 'We hired David for a 7-day private tour of Morocco and it exceeded every expectation. The vehicle was immaculate, David was punctual, professional, and incredibly knowledgeable. He took us to places we never would have found on our own. Absolutely 5-star service from start to finish.'
},
{
  name: 'Rachel & David Levy',
  location: 'Tel Aviv, Israel',
  flag: '🇮🇱',
  tour: 'Genealogy & Heritage Tour',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&q=80',
  alt: 'Rachel Levy, satisfied customer from Israel who took a genealogy and heritage tour in Morocco',
  text: 'David helped us trace our family roots in the Mellah of Fes. It was an incredibly emotional and meaningful journey. He had done research in advance, knew exactly which cemetery to visit, and even helped us find our great-grandmother\'s grave. This was not just a tour — it was a life-changing experience.'
},
{
  name: 'The Goldstein Family',
  location: 'Toronto, Canada',
  flag: '🇨🇦',
  tour: 'Family Morocco Tour',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&q=80',
  alt: 'Goldstein family from Toronto who took a private family tour in Morocco with guide David',
  text: 'We traveled as a family of 6, including grandparents and young children. David was patient, flexible, and made sure everyone was comfortable and engaged. The kosher meal planning was perfect, and the Jewish heritage sites were presented beautifully. An unforgettable family journey!'
},
{
  name: 'Marie & Pierre Dubois',
  location: 'Paris, France',
  flag: '🇫🇷',
  tour: 'Imperial Cities Tour',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&q=80',
  alt: 'Marie Dubois from Paris who took an imperial cities private tour in Morocco with David',
  text: 'David\'s French is perfect and his knowledge of Moroccan history is extraordinary. He took us through Fes, Meknes, Rabat, and Marrakech with such passion and depth. Every meal recommendation was excellent, every hotel suggestion was perfect. C\'est un guide exceptionnel!'
},
{
  name: 'Robert & Helen Thompson',
  location: 'Sydney, Australia',
  flag: '🇦🇺',
  tour: 'Sahara Desert & Imperial Cities',
  rating: 5,
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&q=80',
  alt: 'Robert Thompson from Sydney Australia who took a Sahara Desert and imperial cities tour in Morocco',
  text: 'Coming all the way from Australia, we wanted to make sure every moment counted. David delivered beyond our wildest expectations. The Sahara desert experience was magical, the cities were fascinating, and David\'s personal attention made us feel like VIPs throughout. Worth every penny!'
}];


export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  const prev = () => setActiveIndex((i) => (i - 1 + testimonials?.length) % testimonials?.length);
  const next = () => setActiveIndex((i) => (i + 1) % testimonials?.length);

  return (
    <section id="testimonials" className="section-padding bg-navy relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-10 left-10 text-gold text-9xl font-serif">&ldquo;</div>
        <div className="absolute bottom-10 right-10 text-gold text-9xl font-serif">&rdquo;</div>
      </div>

      <div className="container-luxury relative z-10">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="tag-gold">Testimonials</span>
          <h2 className="section-title mt-4 mb-4 text-white">
            What Our Travelers
            <br />
            <span className="text-gold italic">Say About Us</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4 text-white/70">
            Hundreds of travelers from around the world have trusted David with their Morocco journey.
            Here&apos;s what they have to say.
          </p>
        </div>

        {/* Featured Testimonial */}
        <div className="max-w-4xl mx-auto mb-12 animate-on-scroll">
          <div className="bg-white/5 border border-white/10 p-8 md:p-12 relative">
            {/* Quote Mark */}
            <div className="text-gold text-6xl font-serif leading-none mb-4">&ldquo;</div>

            <p className="font-serif text-xl md:text-2xl text-white/90 leading-relaxed italic mb-8">
              {testimonials?.[activeIndex]?.text}
            </p>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={testimonials?.[activeIndex]?.image}
                  alt={testimonials?.[activeIndex]?.alt}
                  className="w-14 h-14 rounded-full object-cover border-2 border-gold"
                  loading="lazy" />
                
                <div>
                  <div className="font-serif font-bold text-white">
                    {testimonials?.[activeIndex]?.flag} {testimonials?.[activeIndex]?.name}
                  </div>
                  <div className="font-sans text-xs text-gold">{testimonials?.[activeIndex]?.location}</div>
                  <div className="font-sans text-xs text-white/60 mt-0.5">{testimonials?.[activeIndex]?.tour}</div>
                </div>
              </div>
              <div className="flex gap-1">
                {Array.from({ length: testimonials?.[activeIndex]?.rating ?? 0 })?.map((_, i) =>
                <span key={i} className="gold-star">★</span>
                )}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <button
              onClick={prev}
              className="w-10 h-10 border border-white/30 text-white hover:border-gold hover:text-gold transition-colors duration-200 flex items-center justify-center"
              aria-label="Previous testimonial">
              
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex gap-2">
              {testimonials?.map((_, i) =>
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className={`transition-all duration-300 ${
                i === activeIndex ? 'w-8 h-1.5 bg-gold' : 'w-3 h-1.5 bg-white/30 hover:bg-white/60'}`
                }
                aria-label={`Go to testimonial ${i + 1}`} />

              )}
            </div>

            <button
              onClick={next}
              className="w-10 h-10 border border-white/30 text-white hover:border-gold hover:text-gold transition-colors duration-200 flex items-center justify-center"
              aria-label="Next testimonial">
              
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>

        {/* All Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials?.map((t, index) =>
          <div
            key={t?.name}
            className={`testimonial-card animate-on-scroll ${
            index === activeIndex ?
            'border-gold bg-white/10' : 'border-white/10 hover:border-gold/50 hover:bg-white/8'}`
            }
            style={{ transitionDelay: `${index % 3 * 100}ms` }}
            onClick={() => setActiveIndex(index)}>
            
              <div className="flex gap-1 mb-3">
                {Array.from({ length: t?.rating ?? 0 })?.map((_, i) =>
              <span key={i} className="text-gold text-sm">★</span>
              )}
              </div>
              <p className="font-sans text-sm text-white/80 leading-relaxed mb-4 line-clamp-3">
                &ldquo;{t?.text}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <img
                src={t?.image}
                alt={t?.alt}
                className="w-10 h-10 rounded-full object-cover border border-gold/40"
                loading="lazy" />
              
                <div>
                  <div className="font-sans text-xs font-bold text-white">{t?.flag} {t?.name}</div>
                  <div className="font-sans text-xs text-gold/80">{t?.location}</div>
                  <div className="font-sans text-xs text-white/50">{t?.tour}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Review Platforms */}
        <div className="mt-12 text-center animate-on-scroll">
          <p className="font-sans text-xs text-white/50 uppercase tracking-widest mb-4">Also reviewed on</p>
          <div className="flex flex-wrap justify-center gap-6">
            {['TripAdvisor', 'Google Reviews', 'Viator', 'GetYourGuide'].map((platform) =>
            <div key={platform} className="font-sans text-sm font-bold text-white/40 hover:text-gold transition-colors duration-200">
                {platform}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}